import { ChangeEvent, useContext } from "react";
import styled from "@emotion/styled/macro";
import { Typography, tokens, Select } from "@sunrun/experience-ui-components";
import { NumberInput } from "components/atoms/NumberInput";
import { monthsAndDays } from "utils/usageUtils";
import { UsageType } from "constants/usageTypes";
import { SplatProspectResponse } from "providers/Types";
import { UsageContentSummary } from "components/molecules/ConfirmInfo/UsageContentSummary";
import { UsageFormContext } from "providers/UsageFormContext";

const INTERVAL_DATA_DECLINE_REASONS = [
  "Customer declined",
  "Login issues",
  "Customer security concerns",
  "Time constraints",
  "Customer moved in less than 12 months ago",
];

type EnergyUsageFormProps = {
  usageData: (number | undefined)[] | null;
  setUsageData: (data: (number | undefined)[]) => void;
  usageCadence: "monthly" | "daily";
  setUsageCadence: (type: "monthly" | "daily") => void;
  usageType: UsageType;
  setUsageType: (type: UsageType) => void;
  homeSize?: number;
  setHomeSize?: (size: number) => void;
  isReadOnly?: boolean;
  guidance?: React.ReactNode;
  usage?: SplatProspectResponse;
  intervalDataDeclinedReason?: string;
  setIntervalDataDeclinedReason?: (reason: string) => void;
};

const EnergyUsageForm = ({
  usageData,
  setUsageData,
  usageCadence,
  setUsageCadence,
  usageType,
  setUsageType,
  homeSize,
  setHomeSize,
  isReadOnly = false,
  guidance,
  usage,
  intervalDataDeclinedReason,
  setIntervalDataDeclinedReason,
}: EnergyUsageFormProps) => {
  const usageContext = useContext(UsageFormContext);
  const { intervalDeclineReasonError, setIntervalDeclineReasonError } =
    usageContext;
  return (
    <StyledFlex5Container>
      <header>
        <Typography size={tokens.FONT_SIZE_3}>Energy Usage</Typography>
      </header>
      {!isReadOnly && (
        <StyledUsageTypeSelectionContainer>
          <SelectContainer>
            <StyledLabel htmlFor="usage-type-select">Usage Type</StyledLabel>
            <StyledSelect
              value={usageType}
              title="Usage Type"
              options={[
                { label: "12 months", value: "full" },
                { label: "Partial months", value: "partial" },
                { label: "Square footage", value: "squareFootage" },
              ]}
              onChange={({ target }) => setUsageType(target.value as UsageType)}
              data-testid={"usage-type-select"}
            />
          </SelectContainer>
          {usageType !== "squareFootage" && (
            <SelectContainer>
              <StyledLabel htmlFor="usage-cadence-select">
                Data Type
              </StyledLabel>
              <StyledSelect
                value={usageCadence}
                title="Data Type"
                options={[
                  { label: "Monthly", value: "monthly" },
                  { label: "Daily", value: "daily" },
                ]}
                onChange={({ target }) =>
                  setUsageCadence(target.value as "monthly" | "daily")
                }
                data-testid={"usage-cadence-select"}
              />
            </SelectContainer>
          )}
          {setIntervalDataDeclinedReason && (
            <SelectContainer>
              <StyledLabel htmlFor="interval-data-decline-reason-select">
                Interval data decline reason
              </StyledLabel>
              <StyledSelect
                style={{ width: "100%" }}
                value={
                  usage?.intervalDataDeclinedReason
                    ? ""
                    : intervalDataDeclinedReason
                }
                title="Interval data decline reason"
                options={INTERVAL_DATA_DECLINE_REASONS.map((reason) => ({
                  label: reason,
                  value: reason,
                }))}
                onChange={({ target }) => {
                  setIntervalDataDeclinedReason(target.value as string);
                  setIntervalDeclineReasonError(false);
                }}
                data-testid={"interval-data-decline-reason-select"}
                disabled={!!usage?.intervalDataDeclinedReason}
                placeholder={
                  usage?.intervalDataDeclinedReason
                    ? usage.intervalDataDeclinedReason
                    : "Select a reason"
                }
                isError={intervalDeclineReasonError}
                helperText={
                  intervalDeclineReasonError
                    ? "Please select a reason for declining interval data access."
                    : undefined
                }
              />
            </SelectContainer>
          )}
        </StyledUsageTypeSelectionContainer>
      )}
      {guidance}
      <InputsContainer>
        {usageType === "squareFootage" ? (
          <StyledSquareFootageContainer>
            <StyledLabel htmlFor="home-size-input">
              Home Size (sq ft)
            </StyledLabel>
            <StyledNumberInput
              type="number"
              placeholder="Enter home size"
              id="home-size-input"
              value={homeSize || ""}
              aria-label="Home size in square feet"
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                if (setHomeSize) {
                  const value =
                    e.target.value === "" ? 0 : parseInt(e.target.value);
                  setHomeSize(value);
                }
              }}
              name="home-size-input"
              disabled={isReadOnly}
            />
          </StyledSquareFootageContainer>
        ) : (
          <StyledGrid>
            {[
              "January",
              "February",
              "March",
              "April",
              "May",
              "June",
              "July",
              "August",
              "September",
              "October",
              "November",
              "December",
            ].map((monthName, index) => {
              const monthlyVal = usageData ? usageData[index] : undefined;
              return (
                <StyledNumberInputContainer key={monthsAndDays[index].month}>
                  <StyledLabel htmlFor={`${monthsAndDays[index].month}-input`}>
                    {monthsAndDays[index].month}
                  </StyledLabel>
                  <StyledNumberInput
                    type="number"
                    placeholder={
                      usageType === "full" ? "kWh" : "Only enter actual kWh"
                    }
                    id={`${monthsAndDays[index].month}-input`}
                    value={monthlyVal}
                    aria-label={`${monthName} kWh`}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      if (!usageData) {
                        return;
                      }

                      let newArr = [...usageData];
                      const value =
                        e.target.value === "" ? undefined : e.target.value;
                      newArr[index] =
                        value === undefined ? undefined : parseInt(value);
                      setUsageData(newArr);
                    }}
                    name={`${monthsAndDays[index].month}-input`}
                    disabled={isReadOnly}
                  />
                </StyledNumberInputContainer>
              );
            })}
          </StyledGrid>
        )}
        {usage && (
          <UsageContentSummary
            usage={usage}
            usageData={usageData || undefined}
            daily={usageCadence === "daily"}
          />
        )}
      </InputsContainer>
    </StyledFlex5Container>
  );
};

const StyledSelect = styled(Select)`
  width: 240px;
`;

const StyledNumberInput = styled(NumberInput)`
  width: 100% !important;
  min-width: 100% !important;
  height: 60px;
  margin-top: 8px;
  input {
    width: 100% !important;
    min-width: 100% !important;
    text-align: left;
    padding-left: 12px;
    &::placeholder {
      white-space: pre-line;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
    }
  }
`;
const StyledUsageTypeSelectionContainer = styled.div`
  margin-top: 24px;
  display: flex;
  gap: 24px;
  @media (max-width: 550px) {
    flex-direction: column;
  }
`;

const InputsContainer = styled.div`
  background-color: ${tokens.MEDIUMBLUE_90};
  padding: 32px;
  border-radius: 8px;
`;

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, auto);
  grid-column-gap: 18px;
  grid-row-gap: 10px;
  min-width: 330px;
  @media (max-width: 900px) {
    grid-template-columns: repeat(6, auto);
  }
  @media (max-width: 550px) {
    grid-template-columns: repeat(4, auto);
  }
  background-color: ${tokens.MEDIUMBLUE_90};
`;

const SelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledNumberInputContainer = styled.div`
  text-align: left;

  width: 100% !important;
  height: 100px;
  overflow: hidden;

  input {
    width: 100% !important;
    margin: 0;
    height: 68px;
  }
  > input::placeholder {
    white-space: pre-line;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const StyledLabel = styled.label`
  color: ${tokens.OFFWHITE_20};
  font-size: 20px;
  justify-self: left;
  margin-bottom: 8px;
  margin-right: auto;
`;

const StyledFlex5Container = styled.div`
  flex: 5;
  margin-bottom: 40px;
`;

const StyledSquareFootageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export { EnergyUsageForm };
